import {defaultPastelColor, randomPastelColor} from 'lib/Colors';
import {ProteinAnnotationVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * Represents the state of the protein annotation panel for annotation create or edit.
 */
export interface ProteinAnnotationPanelState {
  hoveredAnnotationId: string | null;
  selectedAnnotationId: string | null;
  isCreateActive: boolean;
  name: string;
  annotationType: string;
  // Start and end are model locations, not display locations
  start: number | null;
  end: number | null;
  color: string;
  notes: string;
}

/**
 * Represents what range of amino acids are selected on a protein's sequence map.
 */
export interface ProteinSelectionUIState {
  start: number;
  end: number | null;
}

export const DEFAULT_PROTEIN_ANNOTATION_PANEL_STATE: ProteinAnnotationPanelState = {
  hoveredAnnotationId: null,
  selectedAnnotationId: null,
  isCreateActive: false,
  name: '',
  annotationType: '',
  start: null,
  end: null,
  color: defaultPastelColor,
  notes: '',
};

const DEFAULT_PROTEIN_SELECTION_UI_STATE: ProteinSelectionUIState = {start: 0, end: null};

export const proteinAnnotationUIGlobalState = makeUseGlobalClientState<ProteinAnnotationPanelState>(
  DEFAULT_PROTEIN_ANNOTATION_PANEL_STATE
);
export const proteinSelectionUIGlobalState = makeUseGlobalClientState<ProteinSelectionUIState>(
  DEFAULT_PROTEIN_SELECTION_UI_STATE
);

/**
 * Updates the global UI state for a protein when the annotation create form is opened.
 */
export function openCreateAnnotation(stateId: string, selectionState: ProteinSelectionUIState): void {
  const newState = {
    ...DEFAULT_PROTEIN_ANNOTATION_PANEL_STATE,
    isCreateActive: true,
    color: randomPastelColor(),
    start: selectionState.start,
    end: selectionState.end,
  };
  proteinAnnotationUIGlobalState.updateState(stateId, newState);
}

/**
 * Updates the global UI state for a protein when an annotation is selected.
 */
export function selectAnnotation(stateId: string, annotation: ProteinAnnotationVersion): void {
  const selectionState = {start: annotation.start, end: annotation.end};
  const annotationState = {
    ...DEFAULT_PROTEIN_ANNOTATION_PANEL_STATE,
    selectedAnnotationId: annotation.id,
    isCreateActive: false,
    name: annotation.name,
    annotationType: annotation.type,
    start: annotation.start,
    end: annotation.end,
    color: annotation.color,
    notes: annotation.notes,
  };
  proteinAnnotationUIGlobalState.updateState(stateId, annotationState);
  proteinSelectionUIGlobalState.updateState(stateId, selectionState);
}

/**
 * Updates the global UI state for a protein when annotation create and edit forms are closed
 */
export function closeAnnotationForm(stateId: string): void {
  proteinAnnotationUIGlobalState.updateState(stateId, DEFAULT_PROTEIN_ANNOTATION_PANEL_STATE);
}

/**
 * Updates the global UI state for a protein when a selection is made on the sequence map.
 */
export function selectRange(stateId: string, start: number | null, end: number | null): void {
  const currentSelectionState = proteinSelectionUIGlobalState.getState(stateId);
  if (currentSelectionState.start === start && currentSelectionState.end === end) {
    return;
  }
  const currentAnnotationState = proteinAnnotationUIGlobalState.getState(stateId);
  const selectionStart = start ?? 0;
  const newSelectionState = {
    start: selectionStart,
    end: selectionStart === end ? null : end,
  };
  let annotationState;
  // If the create form is open, mirror the selection state to the annotation location
  if (currentAnnotationState.isCreateActive) {
    annotationState = {
      ...currentAnnotationState,
      selectedAnnotationId: null,
      start: newSelectionState.start,
      end: newSelectionState.end,
    };
  } else {
    annotationState = {...currentAnnotationState, selectedAnnotationId: null};
  }
  proteinAnnotationUIGlobalState.updateState(stateId, annotationState);
  proteinSelectionUIGlobalState.updateState(stateId, newSelectionState);
}

/**
 * Updates the hovered annotation in global UI state.
 */
export function toggleAnnotationHover(stateId: string, annotationId: string, isHovered: boolean): void {
  const currentAnnotationState = proteinAnnotationUIGlobalState.getState(stateId);
  // Only unhover an annotation if it is actually currently hovered
  if (!isHovered && currentAnnotationState.hoveredAnnotationId !== annotationId) {
    return;
  }
  proteinAnnotationUIGlobalState.updateState(stateId, {
    ...currentAnnotationState,
    hoveredAnnotationId: isHovered ? annotationId : null,
  });
}

export function getSelectedAnnotationId(stateId: string): string | null {
  return proteinAnnotationUIGlobalState.getState(stateId).selectedAnnotationId;
}

export function getHoveredAnnotationId(stateId: string): string | null {
  return proteinAnnotationUIGlobalState.getState(stateId).hoveredAnnotationId;
}
